import { useState } from "react";
import { usePageSeo } from "@/hooks/usePageSeo";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import CTASection from "@/components/CTASection";
import Footer from "@/components/Footer";
import { motion } from "framer-motion";
import { Users, Wifi, Snowflake, Briefcase } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import heroFleetImg from "@/assets/hero-fleet.jpg";

const featureIcons: Record<string, typeof Wifi> = {
  "A/C": Snowflake, "Wi-Fi": Wifi, "Luggage Space": Briefcase, "Extra Luggage": Briefcase, "Group Seating": Users, "Large Capacity": Users,
};

const FleetPage = () => {
  usePageSeo("fleet");
  const [activeTab, setActiveTab] = useState<string>("all");

  const { data: vehicles, isLoading } = useQuery({
    queryKey: ["fleet-vehicles"],
    queryFn: async () => {
      const { data, error } = await supabase.from("vehicles").select("*, service_types(name)").eq("is_active", true).order("sort_order");
      if (error) throw error;
      return data;
    },
  });

  const serviceTypes = vehicles ? Array.from(new Set(vehicles.map((v) => (v.service_types as any)?.name || "Other"))) : [];
  const filteredVehicles = activeTab === "all" ? vehicles : vehicles?.filter((v) => ((v.service_types as any)?.name || "Other") === activeTab);

  return (
    <main className="min-h-screen bg-background">
      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <img src={heroFleetImg} alt="Our Fleet" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-white/75" />
        
        <Navbar />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }}>
              <p className="text-primary font-semibold tracking-wide uppercase text-sm mb-3">Premium Fleet</p>
              <h1 className="font-display text-5xl md:text-7xl font-extrabold text-foreground mb-6 leading-tight">
                Choose Your Perfect Ride
              </h1>
              <p className="text-muted-foreground text-xl leading-relaxed max-w-2xl mb-8">
                From luxury sedans to spacious minibuses — our premium fleet ensures comfort for every journey across the Dominican Republic.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Fleet */}
      <section className="py-20 md:py-28 bg-background">
        <div className="container mx-auto px-4">
          {serviceTypes.length > 1 && (
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              <button onClick={() => setActiveTab("all")}
                className={`px-6 py-2.5 rounded-lg font-semibold text-sm transition-all ${activeTab === "all" ? "bg-primary text-primary-foreground shadow-sm" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}>
                All Vehicles
              </button>
              {serviceTypes.map((type) => (
                <button key={type} onClick={() => setActiveTab(type)}
                  className={`px-6 py-2.5 rounded-lg font-semibold text-sm transition-all ${activeTab === type ? "bg-primary text-primary-foreground shadow-sm" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}>
                  {type}
                </button>
              ))}
            </div>
          )}

          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="rounded-lg overflow-hidden border border-border">
                  <Skeleton className="h-52 w-full" />
                  <div className="p-5 space-y-3"><Skeleton className="h-6 w-32" /><Skeleton className="h-4 w-full" /></div>
                </div>
              ))}
            </div>
          ) : filteredVehicles && filteredVehicles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredVehicles.map((vehicle, index) => (
                <motion.div key={vehicle.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.08 }}
                  layout
                  className="group rounded-lg overflow-hidden bg-card border border-border hover:border-primary/30 hover:shadow-md transition-all duration-300">
                  <div className="relative overflow-hidden h-52">
                    <img src={vehicle.image_url || "/placeholder.svg"} alt={vehicle.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    {vehicle.badge && (
                      <div className="absolute top-3 left-3 bg-primary text-primary-foreground text-xs font-bold px-3 py-1.5 rounded-md">{vehicle.badge}</div>
                    )}
                    <div className="absolute top-3 right-3 bg-foreground/80 text-background text-xs font-bold px-3 py-1.5 rounded-md">
                      <Users className="w-3 h-3 inline mr-1" />{vehicle.min_passengers}-{vehicle.capacity} pax
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-display text-xl font-bold text-foreground mb-2">{vehicle.name}</h3>
                    {vehicle.description && <p className="text-muted-foreground text-sm leading-relaxed mb-4">{vehicle.description}</p>}
                    {vehicle.features && vehicle.features.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {vehicle.features.map((f) => {
                          const Icon = featureIcons[f] || Wifi;
                          return <span key={f} className="inline-flex items-center gap-1 text-xs font-medium text-primary bg-primary/10 px-2.5 py-1 rounded-md"><Icon className="w-3 h-3" /> {f}</span>;
                        })}
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16"><p className="text-muted-foreground text-lg">No vehicles available at the moment.</p></div>
          )}
        </div>
      </section>

      <CTASection />
      <Footer />
    </main>
  );
};

export default FleetPage;
